import type { InfiniteData } from '@reduxjs/toolkit/query';
import type { TAppThunk } from 'slices/store';
import type { TTEObject } from '@timeedit/types';
import { objectApi } from './objectApi';
import type { TObjectFindResponse } from './objectApi.types';

type TListData = InfiniteData<TObjectFindResponse, number>;

// The list is a snapshot the user reloads on purpose (the reload alert, a filter change). A save
// or delete changes the rows in the cached pages instead of reloading them, so scroll, sort and
// every other row stay as they were.
const patchCachedLists =
  (patch: (list: TListData) => void): TAppThunk =>
  (dispatch, getState) => {
    for (const arg of objectApi.util.selectCachedArgsForQuery(
      getState(),
      'findObjectList'
    )) {
      dispatch(objectApi.util.updateQueryData('findObjectList', arg, patch));
    }
  };

const pageAndIndexOf = (list: TListData, id: number) => {
  for (const page of list.pages) {
    const index = page.results.findIndex((row) => row.id === id);
    if (index >= 0) return { page, index };
  }
  return null;
};

export const replaceObjectInLists = (object: TTEObject): TAppThunk =>
  patchCachedLists((list) => {
    const found = pageAndIndexOf(list, object.id);
    if (found) found.page.results[found.index] = object;
  });

// A new object stays at the top until a reload sorts it in, as it was while being edited
export const addObjectToLists = (object: TTEObject): TAppThunk =>
  patchCachedLists((list) => {
    if (pageAndIndexOf(list, object.id)) return;
    list.pages[0]?.results.unshift(object);
    for (const page of list.pages) page.totalResults += 1;
  });

export const removeObjectFromLists = (id: number): TAppThunk =>
  patchCachedLists((list) => {
    const found = pageAndIndexOf(list, id);
    if (!found) return;
    found.page.results.splice(found.index, 1);
    for (const page of list.pages) page.totalResults -= 1;
  });
